import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTIES, GET_USERS, GET_GUEST_ALERT_RESERVATION_CANDIDATE } from '../../graphql/queries';
import { UPDATE_WORK_ORDER } from '../../graphql/mutations';
import { GET_ACTIVE_TEAMS } from '../../graphql/teams';
import { GET_ACTIVE_WORK_ORDER_FLAG_TEMPLATES } from '../../graphql/work-order-flags';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../config';
import { UserRole, UserRoleLabels } from '../../utils/enums';
import { canBeAssignedWorkOrders } from '../../utils/role-utils';
import { useAuth } from '../../contexts/AuthContext';
import VideoUploader from './VideoUploader';
import { FlagIcon } from '../../utils/flag-icons';
import { calculateTintedFlagTextColor } from '../../utils/custom-flag-utils';
import { WorkOrderType, getAssignableTeamDepartment, getLegacyWorkOrderType, getWorkOrderTypeOption, getWorkOrderTypeOptionsForRole, isPropertyManagerAssignedType } from '../../utils/work-order-types';
import { uploadWorkOrderPhoto } from '../../utils/work-order-photo-upload';
import { getTeamDepartmentLabel } from '../team-members/teams-constants';

const ITEMS_PER_PAGE = 20; // Maximum allowed by backend pagination
const WORK_ORDER_DRAFT_PREFIX = 'sweetgrass-work-order-draft';

const readWorkOrderDraft = (storageKey) => {
  if (!storageKey) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
  } catch {
    return {};
  }
};

const WorkOrderForm = forwardRef(({
  initialData,
  onSubmit,
  isLoading,
  hideButtons = false,
  showAssignment = true,
  onUploadStateChange,
}, ref) => {
  const draftStorageKey = initialData?.id
    ? null
    : `${WORK_ORDER_DRAFT_PREFIX}:${initialData?.propertyId || 'new'}`;
  
  // Helper function to safely format date for input field
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    
    try {
      if (typeof dateValue === 'string') {
        // If it's already a YYYY-MM-DD string, return as is
        if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue;
        }
        // If it's an ISO string, extract the date part
        if (dateValue.includes('T')) {
          return dateValue.split('T')[0];
        }
        // Try to parse as date and format
        return new Date(dateValue).toISOString().split('T')[0];
      } else if (dateValue instanceof Date) {
        return dateValue.toISOString().split('T')[0];
      }
      return '';
    } catch (error) {
      console.error('Error formatting date for input:', error, dateValue);
      return '';
    }
  };
  const { user } = useAuth();
  
  // Check if current user can see assignment section
  const canViewAssignment = () => {
    if (!user?.role) return false;
    const allowedRoles = ['ADMIN', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER'];
    return allowedRoles.includes(user.role);
  };

  const canManageWorkOrderFlags = () => {
    if (!user?.role) return false;
    return ['ADMIN', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'CALL_CENTER', 'MAINTENANCE'].includes(user.role);
  };
  
  const [formData, setFormData] = useState(() => ({
    propertyId: initialData?.propertyId || '',
    reservationId: initialData?.reservationId || '',
    description: initialData?.description || '',
    isUrgent: initialData?.isUrgent || false,
    isAfterHours: initialData?.isAfterHours || false,
    isFeedback: initialData?.isFeedback || false,
    workOrderType: getLegacyWorkOrderType(initialData),
    visibilityRoles: initialData?.visibilityRoles || ['ADMIN'], // Default to admin only
    assignedToId: initialData?.assignedToId || '',
    assignedTeamId: initialData?.assignedTeamId || '',
    assignmentType: initialData?.assignedTeamId ? 'team' : 'person',
    assignedRoles: initialData?.assignedRoles || [],
    photos: initialData?.photos || [],
    videos: initialData?.videos || [],
    dueDate: initialData?.dueDate || '',
    workOrderFlagTemplateIds: (initialData?.customFlags || []).map(flag => flag.templateId).filter(Boolean),
    guestAlertsEnabled: initialData?.guestAlertsEnabled || false,
    guestAlertDescription: initialData?.guestAlertDescription || '',
    guestAlertUseContactOverride: false,
    guestAlertEmailOverride: '',
    guestAlertPhoneOverride: '',
    // Spread other properties from initialData, but preserve the specific fields above
    ...Object.fromEntries(
      Object.entries(initialData || {}).filter(([key]) => 
        !['propertyId', 'reservationId', 'description', 'isUrgent', 'isAfterHours', 'isFeedback', 'workOrderType', 'visibilityRoles', 'assignedToId', 'assignedTeamId', 'assignmentType', 'assignedRoles', 'photos', 'videos', 'dueDate', 'customFlags', 'workOrderFlagTemplateIds'].includes(key)
      )
    ),
    ...readWorkOrderDraft(draftStorageKey),
  }));
  const [errors, setErrors] = useState({});
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [photoUploads, setPhotoUploads] = useState([]);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const uploadAbortControllerRef = useRef(null);
  const guestAlertsTouchedRef = useRef(false);

  const [updateWorkOrder] = useMutation(UPDATE_WORK_ORDER);

  useEffect(() => {
    if (!draftStorageKey) return;
    try {
      window.sessionStorage.setItem(draftStorageKey, JSON.stringify(formData));
    } catch {
      // Draft persistence is best-effort (private browsing may block storage).
    }
  }, [draftStorageKey, formData]);

  const clearDraft = () => {
    if (!draftStorageKey) return;
    try {
      window.sessionStorage.removeItem(draftStorageKey);
    } catch {
      // Nothing else is required if storage is unavailable.
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      if (isUploading) {
        toast.error('Please wait for the photo upload to finish before submitting.');
        return false;
      }
      if (validateForm()) {
        const submitted = await onSubmit(formData);
        if (submitted !== false) clearDraft();
        return submitted !== false;
      }
      return false;
    }
  }));

  const { data: propertiesData } = useQuery(GET_PROPERTIES, {
    variables: {
      limit: ITEMS_PER_PAGE,
      page: 1,
      search: propertySearchTerm || undefined,
      startDate: new Date().toISOString().split('T')[0], // Today
      endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 90 days from now
    },
    fetchPolicy: 'network-only',
  });

  // Query to get users for assignment (only shown when creating new work orders and user has permission)
  const { data: usersData } = useQuery(GET_USERS, {
    skip: !showAssignment || !canViewAssignment(), // Only fetch when assignment is enabled and user has permission
    fetchPolicy: 'cache-first'
  });

  const assignableTeamDepartment = getAssignableTeamDepartment(formData.workOrderType);
  const { data: teamsData } = useQuery(GET_ACTIVE_TEAMS, {
    skip: !showAssignment || !canViewAssignment() || isPropertyManagerAssignedType(formData.workOrderType),
    variables: { department: assignableTeamDepartment },
    fetchPolicy: 'cache-first'
  });

  const { data: workOrderFlagTemplatesData } = useQuery(GET_ACTIVE_WORK_ORDER_FLAG_TEMPLATES, {
    fetchPolicy: 'cache-first'
  });

  const isGuestAlertWorkOrderType = [WorkOrderType.GUEST_IMPACT, WorkOrderType.HOUSEKEEPING]
    .includes(formData.workOrderType);
  const { data: guestAlertCandidateData, loading: guestAlertCandidateLoading } = useQuery(
    GET_GUEST_ALERT_RESERVATION_CANDIDATE,
    {
      variables: { propertyId: formData.propertyId },
      skip: Boolean(initialData?.id) || !formData.propertyId || !isGuestAlertWorkOrderType,
      fetchPolicy: 'network-only',
    },
  );
  const guestAlertCandidate = guestAlertCandidateData?.guestAlertReservationCandidate;

  useEffect(() => {
    if (initialData?.id || guestAlertsTouchedRef.current || user?.role !== 'CALL_CENTER') return;
    if (guestAlertCandidate?.available && isGuestAlertWorkOrderType) {
      setFormData(previous => ({
        ...previous,
        guestAlertsEnabled: true,
        reservationId: guestAlertCandidate.reservationId || previous.reservationId,
      }));
    }
  }, [guestAlertCandidate, initialData?.id, isGuestAlertWorkOrderType, user?.role]);

  const workOrderFlagTemplates = workOrderFlagTemplatesData?.getActiveWorkOrderFlagTemplates || [];

  useEffect(() => {
    if (initialData?.propertyId && propertiesData?.propertiesWithReservations?.properties) {
      const property = propertiesData.propertiesWithReservations.properties.find(
        p => p.id === initialData.propertyId
      );
      if (property) {
        setSelectedProperty(property);
        setPropertySearchTerm(property.name);
      }
    }
    
    // Also support directly using the property from initialData
    if (initialData?.property && initialData?.property.id) {
      setSelectedProperty(initialData.property);
      setPropertySearchTerm(initialData.property.name || 'Unknown Property');
    }
    
    // Use propertySearchTerm from initialData if provided
    if (initialData?.propertySearchTerm) {
      setPropertySearchTerm(initialData.propertySearchTerm);
    }
  }, [initialData, propertiesData]);

  // Update form data when initialData changes (for editing work orders)
  useEffect(() => {
    if (initialData) {
      const savedDraft = initialData.id ? {} : readWorkOrderDraft(draftStorageKey);
      setFormData(prevData => ({
        ...prevData,
        propertyId: initialData.propertyId || prevData.propertyId,
        reservationId: initialData.reservationId || prevData.reservationId,
        description: initialData.description || prevData.description,
        isUrgent: initialData.isUrgent || prevData.isUrgent,
        isAfterHours: initialData.isAfterHours || prevData.isAfterHours,
        isFeedback: initialData.isFeedback ?? prevData.isFeedback,
        workOrderType: getLegacyWorkOrderType(initialData),
        visibilityRoles: initialData.visibilityRoles || prevData.visibilityRoles,
        assignedToId: initialData.assignedToId || prevData.assignedToId,
        assignedTeamId: initialData.assignedTeamId || prevData.assignedTeamId,
        assignmentType: initialData.assignedTeamId ? 'team' : (prevData.assignmentType || 'person'),
        assignedRoles: initialData.assignedRoles || prevData.assignedRoles,
        photos: initialData.photos || prevData.photos,
        videos: initialData.videos || prevData.videos,
        dueDate: initialData.dueDate || prevData.dueDate,
        workOrderFlagTemplateIds: initialData.workOrderFlagTemplateIds ||
          (initialData.customFlags || []).map(flag => flag.templateId).filter(Boolean) ||
          prevData.workOrderFlagTemplateIds,
        // Spread other properties from initialData, but preserve existing values for specific fields
        ...Object.fromEntries(
          Object.entries(initialData).filter(([key]) => 
            !['propertyId', 'reservationId', 'description', 'isUrgent', 'isAfterHours', 'isFeedback', 'workOrderType', 'visibilityRoles', 'assignedToId', 'assignedTeamId', 'assignmentType', 'assignedRoles', 'photos', 'videos', 'dueDate', 'customFlags', 'workOrderFlagTemplateIds'].includes(key)
          )
        ),
        ...savedDraft,
      }));
    }
  }, [draftStorageKey, initialData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.propertyId) {
      newErrors.propertyId = 'Property is required';
    } else if (!formData.propertyId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      newErrors.propertyId = 'Invalid property ID format';
      // console.error('Invalid propertyId in validateForm:', formData.propertyId);
    }
    
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }

    if (!formData.workOrderType) {
      newErrors.workOrderType = 'Work order type is required';
    }

    if (formData.guestAlertsEnabled) {
      if (!isGuestAlertWorkOrderType || !guestAlertCandidate?.available) {
        newErrors.guestAlertsEnabled = guestAlertCandidate?.reason || 'Guest Alerts are not available for this work order.';
      }
      if (formData.guestAlertEmailOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestAlertEmailOverride)) {
        newErrors.guestAlertEmailOverride = 'Enter a valid email address.';
      }
      if (formData.guestAlertPhoneOverride && formData.guestAlertPhoneOverride.replace(/\D/g, '').length < 10) {
        newErrors.guestAlertPhoneOverride = 'Enter a valid phone number.';
      }
    }
    
    // console.log('Form validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) {
      toast.error('Please wait for the photo upload to finish before submitting.');
      return;
    }
    if (validateForm()) {
      const submitted = await onSubmit(formData);
      if (submitted !== false) clearDraft();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleWorkOrderFlagToggle = (templateId) => {
    setFormData(prev => {
      const currentIds = prev.workOrderFlagTemplateIds || [];
      const isSelected = currentIds.includes(templateId);
      return {
        ...prev,
        workOrderFlagTemplateIds: isSelected
          ? currentIds.filter(id => id !== templateId)
          : [...currentIds, templateId]
      };
    });
  };

  const handlePropertySelect = (property, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // console.log('Selected property:', property);
    
    if (!property || !property.id) {
      // console.error('Selected property is missing ID');
      return;
    }
    
    setSelectedProperty(property);
    
    // Ensure propertyId is set correctly
    const newData = {
      ...formData,
      propertyId: property.id
    };
    // console.log('Setting formData with propertyId:', property.id);
    setFormData(newData);
    
    setPropertySearchTerm(property.name);
    
    // Close the dropdown after selection
    setTimeout(() => {
      setIsPropertyDropdownOpen(false);
    }, 100);
    
    // Clear error when field is edited
    if (errors.propertyId) {
      setErrors(prevErrors => ({
        ...prevErrors,
        propertyId: null
      }));
    }
  };

  const handlePropertySearchChange = (e) => {
    const value = e.target.value;
    setPropertySearchTerm(value);
    setIsPropertyDropdownOpen(true);
    
    // Ensure dropdown stays open when typing
    if (value.trim().length > 0) {
      setIsPropertyDropdownOpen(true);
    }
  };

  const filteredProperties = propertiesData?.propertiesWithReservations?.properties?.filter(property => {
    if (!propertySearchTerm.trim()) return true;
    const search = propertySearchTerm.toLowerCase();
    return (
      property.name.toLowerCase().includes(search) || 
      property.address.toLowerCase().includes(search) ||
      (property.city && property.city.toLowerCase().includes(search)) ||
      (property.propertyCode && property.propertyCode.toLowerCase().includes(search))
    );
  }) || [];

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    setIsUploading(true);
    onUploadStateChange?.(true);
    setUploadProgress(0);
    setPhotoUploadError('');
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    const uploadedUrls = [];
    const uploads = acceptedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      status: 'preparing',
      progress: 0,
      error: '',
    }));
    setPhotoUploads(uploads);

    const updateUpload = (id, changes) => {
      setPhotoUploads((current) => current.map((item) => (
        item.id === id ? { ...item, ...changes } : item
      )));
    };

    for (const [index, file] of acceptedFiles.entries()) {
      const uploadId = uploads[index].id;
      try {
        let processedFile = file;

        // Handle HEIC/HEIF conversion if needed
        if (file.type === 'image/heic' || file.type === 'image/heif' || 
            file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          toast.loading(`Preparing iPhone photo ${index + 1}/${acceptedFiles.length}...`, { id: "heic-conversion" });
          
          try {
            const heic2any = (await import('heic2any')).default;
            const converted = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.8
            });
            const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
            
            processedFile = new File(
              [jpegBlob],
              file.name.replace(/\.(heic|heif)$/i, '.jpg'),
              { type: 'image/jpeg' }
            );
            
            toast.success(`iPhone photo ${index + 1} is ready`, { id: "heic-conversion" });
          } catch (conversionError) {
            toast.dismiss("heic-conversion");
            throw new Error(`Unable to prepare ${file.name}. Open it in Photos, let it download, and try again.`);
          }
        }

        const imageUrl = await uploadWorkOrderPhoto({
          apiUrl: API_URL,
          file: processedFile,
          retries: 1,
          signal: abortController.signal,
          onProgress: (progress, status) => {
            updateUpload(uploadId, { progress, status });
            const completedBefore = index / acceptedFiles.length;
            setUploadProgress(Math.round((completedBefore + (progress / 100 / acceptedFiles.length)) * 100));
          },
        });

        uploadedUrls.push(imageUrl);
        updateUpload(uploadId, { status: 'complete', progress: 100 });

      } catch (error) {
        const message = error?.message || `Failed to upload ${file.name}. Please try again.`;
        updateUpload(uploadId, { status: 'failed', error: message });
        setPhotoUploadError(message);
        toast.error(message);
        if (error?.code === 'CANCELED') break;
      }
    }

    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        photos: Array.from(new Set([...(prev.photos || []), ...uploadedUrls]))
      }));
      
      setUploadProgress(100);
      
      if (uploadedUrls.length === 1) {
        toast.success("Image uploaded successfully");
      } else {
        toast.success(`${uploadedUrls.length} images uploaded successfully`);
      }
    }

    setIsUploading(false);
    onUploadStateChange?.(false);
    if (uploadAbortControllerRef.current === abortController) {
      uploadAbortControllerRef.current = null;
    }
    setImagePreview(null);
  }, [onUploadStateChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.webp']
    },
    maxFiles: 10, // Allow up to 10 photos
    multiple: true
  });

  const handleRemovePhoto = (indexToRemove) => {
    const updatedPhotos = formData.photos.filter((_, index) => index !== indexToRemove);
    setFormData(prev => ({
      ...prev,
      photos: updatedPhotos
    }));
  };

  // Handle assignment change - Direct user assignment (independent of roles)
  const handleAssignmentChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      assignmentType: 'person',
      assignedToId: value ? parseInt(value, 10) : '',
      assignedTeamId: ''
    });
  };

  const handleTeamAssignmentChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      assignmentType: 'team',
      assignedTeamId: value ? parseInt(value, 10) : '',
      assignedToId: ''
    });
  };

  const handleAssignmentTypeChange = (assignmentType) => {
    setFormData((prev) => ({
      ...prev,
      assignmentType,
      assignedToId: assignmentType === 'person' ? prev.assignedToId : '',
      assignedTeamId: assignmentType === 'team' ? prev.assignedTeamId : ''
    }));
  };

  const handleWorkOrderTypeSelect = (workOrderType) => {
    const option = getWorkOrderTypeOption(workOrderType);
    setFormData(prev => {
      const shouldClearTeamAssignment = isPropertyManagerAssignedType(workOrderType)
        || getAssignableTeamDepartment(workOrderType) !== getAssignableTeamDepartment(prev.workOrderType);
      return {
        ...prev,
        workOrderType,
        assignedRoles: option.assignedRoles,
        isFeedback: workOrderType === WorkOrderType.FEEDBACK,
        isUrgent: workOrderType === WorkOrderType.PROPERTY_CARE ? false : prev.isUrgent,
        assignedToId: isPropertyManagerAssignedType(workOrderType) ? '' : prev.assignedToId,
        assignedTeamId: shouldClearTeamAssignment ? '' : prev.assignedTeamId,
        assignmentType: isPropertyManagerAssignedType(workOrderType) ? 'person' : prev.assignmentType,
        guestAlertsEnabled: [WorkOrderType.GUEST_IMPACT, WorkOrderType.HOUSEKEEPING].includes(workOrderType)
          ? prev.guestAlertsEnabled
          : false,
      };
    });
    setErrors(prev => ({ ...prev, workOrderType: undefined }));
  };

  const handleGuestAlertsToggle = (event) => {
    guestAlertsTouchedRef.current = true;
    const checked = event.target.checked;
    setFormData(previous => ({
      ...previous,
      guestAlertsEnabled: checked,
      reservationId: checked ? (guestAlertCandidate?.reservationId || previous.reservationId) : previous.reservationId,
    }));
  };

  // Kept temporarily for the hidden legacy department markup below. The new
  // type selector is the only department-routing control shown to users.
  const handleDepartmentAssignment = (department) => handleWorkOrderTypeSelect(
    department === 'housekeeping' ? WorkOrderType.HOUSEKEEPING : WorkOrderType.GUEST_IMPACT
  );
  const isDepartmentSelected = (department) => department === 'housekeeping'
    ? formData.workOrderType === WorkOrderType.HOUSEKEEPING
    : formData.workOrderType === WorkOrderType.GUEST_IMPACT;

  // Roles that never file certain types (for example Inspectors and feedback)
  // do not see those options, unless the work order is already set to that type.
  const roleWorkOrderTypeOptions = getWorkOrderTypeOptionsForRole(user?.role);
  const workOrderTypeOptions = !formData.workOrderType
    || roleWorkOrderTypeOptions.some(option => option.value === formData.workOrderType)
    ? roleWorkOrderTypeOptions
    : [...roleWorkOrderTypeOptions, getWorkOrderTypeOption(formData.workOrderType)];

  const workOrderTypeSection = (
    <div className="sm:col-span-6">
      <label className="flex items-center text-sm font-medium text-gray-700">
        What type of issue is this? <span className="text-red-500 ml-1">*</span>
      </label>
      <p className="mb-2 mt-1 text-xs text-gray-500">
        The selected type controls who receives the work order and how it is prioritized.
      </p>
      {errors.workOrderType && <p className="mb-2 text-sm text-red-600">{errors.workOrderType}</p>}
      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${errors.workOrderType ? 'rounded-lg border border-red-300 p-3' : ''}`}>
        {workOrderTypeOptions.map(option => {
          const isSelected = formData.workOrderType === option.value;
          const selectedClasses = {
            blue: 'border-blue-500 bg-blue-50 text-blue-900',
            amber: 'border-amber-500 bg-amber-50 text-amber-900',
            emerald: 'border-emerald-500 bg-emerald-50 text-emerald-900',
            purple: 'border-purple-500 bg-purple-50 text-purple-900',
          };
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleWorkOrderTypeSelect(option.value)}
              className={`w-full rounded-lg border-2 px-4 py-4 text-left transition-all ${
                isSelected
                  ? `${selectedClasses[option.color]} shadow-sm`
                  : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50'
              }`}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{option.label}</div>
                  <div className="mt-1 text-xs leading-5 text-gray-600">{option.description}</div>
                  <div className="mt-2 text-xs font-medium text-gray-700">Routes to: {option.routingLabel}</div>
                </div>
                {isSelected && (
                  <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        {/* Property Selection */}
        <div className="sm:col-span-3">
          <label htmlFor="property" className="flex items-center text-sm font-medium text-gray-700">
            <svg className="h-4 w-4 mr-1.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Property <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="mt-1 relative">
            <input
              type="text"
              id="property"
              name="property"
              value={propertySearchTerm}
              onChange={handlePropertySearchChange}
              onFocus={() => setIsPropertyDropdownOpen(true)}
              onBlur={(e) => {
                // Only close the dropdown if we're not clicking on a property
                // Use a longer timeout to ensure click events register first
                setTimeout(() => {
                  // Check if we've selected a property
                  if (!selectedProperty || selectedProperty.id !== formData.propertyId) {
                    setIsPropertyDropdownOpen(false);
                  }
                }, 300);
              }}
              placeholder="Search for a property"
              className={`block w-full rounded-md shadow-sm px-3 py-2 ${
                errors.propertyId
                  ? 'border border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500'
                  : 'border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {selectedProperty && (
              <div className="mt-2 flex items-center bg-blue-50 border border-blue-200 rounded-md p-2.5 shadow-sm">
                <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium text-blue-900 flex-grow">
                  {selectedProperty.name} - {selectedProperty.address}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedProperty(null);
                    setFormData({ ...formData, propertyId: '' });
                    setPropertySearchTerm('');
                  }}
                  className="text-blue-500 hover:text-blue-700 ml-2"
                  aria-label="Clear selected property"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {errors.propertyId && (
              <div className="mt-2 flex items-center">
                <svg className="h-5 w-5 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{errors.propertyId}</p>
              </div>
            )}
            {isPropertyDropdownOpen && propertySearchTerm.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md max-h-60 overflow-auto">
                {filteredProperties.length > 0 ? (
                  <ul className="py-1 text-base ring-1 ring-black ring-opacity-5">
                    {filteredProperties.map((property) => (
                      <li
                        key={property.id}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur event from closing dropdown too early
                          handlePropertySelect(property, e);
                        }}
                        className="cursor-pointer hover:bg-gray-100 px-4 py-2 text-sm text-gray-900"
                      >
                        <div className="font-medium">{property.name}</div>
                        <div className="text-xs text-gray-500">
                          {property.address}
                          {property.city && `, ${property.city}`}
                          {property.state && `, ${property.state}`}
                          {property.propertyCode && ` (${property.propertyCode})`}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-4 text-sm text-gray-500">No properties found matching "{propertySearchTerm}"</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="sm:col-span-6">
          <label htmlFor="description" className="flex items-center text-sm font-medium text-gray-700">
            <svg className="h-4 w-4 mr-1.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Description <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="mt-1">
            <textarea
              id="description"
              name="description"
              rows={5}
              value={formData.description}
              onChange={handleInputChange}
              className={`block w-full rounded-md shadow-sm px-3 py-2 ${
                errors.description
                  ? 'border border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500'
                  : 'border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="Describe the issue or work that needs to be done"
            />
            {errors.description && (
              <div className="mt-2 flex items-center">
                <svg className="h-5 w-5 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{errors.description}</p>
              </div>
            )}
          </div>
        </div>

        {workOrderTypeSection}

        {/* Priority Flags */}
        <div className="sm:col-span-6">
          <div className="grid grid-cols-1 gap-3">
            <label
              htmlFor="isUrgent"
              className={`flex items-center gap-3 rounded-lg border px-3 py-3 transition-all duration-150 ${
                formData.workOrderType === WorkOrderType.PROPERTY_CARE
                  ? 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-70'
                  : 'cursor-pointer'
              } ${
                formData.isUrgent
                  ? 'border-red-400 bg-red-50'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <input
                id="isUrgent"
                name="isUrgent"
                type="checkbox"
                checked={formData.isUrgent}
                onChange={handleInputChange}
                disabled={formData.workOrderType === WorkOrderType.PROPERTY_CARE}
                className="h-4 w-4 shrink-0 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <svg
                className={`h-5 w-5 shrink-0 ${formData.isUrgent ? 'text-red-600' : 'text-gray-400'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="min-w-0">
                <span className={`block text-sm font-semibold leading-5 ${formData.isUrgent ? 'text-red-900' : 'text-gray-900'}`}>
                  Urgent
                </span>
                <span className={`block text-xs leading-4 ${formData.isUrgent ? 'text-red-700' : 'text-gray-500'}`}>
                  {formData.workOrderType === WorkOrderType.PROPERTY_CARE
                    ? 'Property Care items cannot be marked urgent.'
                    : 'Use for immediate safety, access, utility, or active property-damage concerns.'}
                </span>
              </div>
            </label>

            <label
              htmlFor="isAfterHours"
              className={`flex items-center gap-3 rounded-lg border px-3 py-3 cursor-pointer transition-all duration-150 ${
                formData.isAfterHours
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <input
                id="isAfterHours"
                name="isAfterHours"
                type="checkbox"
                checked={formData.isAfterHours}
                onChange={handleInputChange}
                className="h-4 w-4 shrink-0 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <svg
                className={`h-5 w-5 shrink-0 ${formData.isAfterHours ? 'text-indigo-600' : 'text-gray-400'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <div className="min-w-0">
                <span className={`block text-sm font-semibold leading-5 ${formData.isAfterHours ? 'text-indigo-900' : 'text-gray-900'}`}>
                  After Hours
                </span>
                <span className={`block text-xs leading-4 ${formData.isAfterHours ? 'text-indigo-700' : 'text-gray-500'}`}>
                  Flag work that needs to be performed outside normal business hours (evenings, weekends, or holidays).
                </span>
              </div>
            </label>
          </div>
        </div>

        {!initialData?.id && (
          <div className="sm:col-span-6">
            <div className={`rounded-lg border p-4 ${
              formData.guestAlertsEnabled ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-white'
            }`}>
              <label className={`flex items-start gap-3 ${
                guestAlertCandidate?.available && isGuestAlertWorkOrderType ? 'cursor-pointer' : 'cursor-not-allowed'
              }`}>
                <input
                  type="checkbox"
                  name="guestAlertsEnabled"
                  checked={formData.guestAlertsEnabled}
                  onChange={handleGuestAlertsToggle}
                  disabled={guestAlertCandidateLoading || !isGuestAlertWorkOrderType || !guestAlertCandidate?.available}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">Guest Alerts</div>
                  <p className="mt-1 text-xs leading-5 text-gray-600">
                    Send warm email and SMS updates when this request is created, first assigned, and completed.
                  </p>
                </div>
              </label>

              {guestAlertCandidateLoading && isGuestAlertWorkOrderType && (
                <p className="mt-3 text-sm text-gray-500">Checking for a currently checked-in guest…</p>
              )}
              {!isGuestAlertWorkOrderType && (
                <p className="mt-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
                  Guest Alerts are available only for Standard Work Order and Housekeeping work orders.
                </p>
              )}
              {isGuestAlertWorkOrderType && !guestAlertCandidateLoading && guestAlertCandidate && !guestAlertCandidate.available && (
                <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {guestAlertCandidate.reason}
                </p>
              )}

              {guestAlertCandidate?.available && (
                <div className="mt-4 rounded-md border border-emerald-200 bg-white p-3">
                  <div className="text-sm font-medium text-gray-900">Current guest: {guestAlertCandidate.guestName}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    {guestAlertCandidate.maskedEmail && <span>Email: {guestAlertCandidate.maskedEmail}</span>}
                    {guestAlertCandidate.maskedPhone && <span>Phone: {guestAlertCandidate.maskedPhone}</span>}
                    {guestAlertCandidate.checkoutAt && (
                      <span>Alerts stop at checkout: {new Date(guestAlertCandidate.checkoutAt).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              )}

              {formData.guestAlertsEnabled && guestAlertCandidate?.available && (
                <div className="mt-4 space-y-4 border-t border-emerald-200 pt-4">
                  <div>
                    <label htmlFor="guestAlertDescription" className="block text-sm font-medium text-gray-700">
                      Guest-facing description <span className="font-normal text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      id="guestAlertDescription"
                      name="guestAlertDescription"
                      rows={2}
                      maxLength={500}
                      value={formData.guestAlertDescription}
                      onChange={handleInputChange}
                      placeholder={`Maintenance Support Request for: ${selectedProperty?.name || 'Property Name'}`}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      If blank, the guest receives “Maintenance Support Request for: {selectedProperty?.name || 'Property Name'}.”
                    </p>
                  </div>

                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.guestAlertUseContactOverride}
                      onChange={(event) => setFormData(previous => ({
                        ...previous,
                        guestAlertUseContactOverride: event.target.checked,
                        guestAlertEmailOverride: event.target.checked ? previous.guestAlertEmailOverride : '',
                        guestAlertPhoneOverride: event.target.checked ? previous.guestAlertPhoneOverride : '',
                      }))}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-800">Use a different guest contact</div>
                      <p className="text-xs text-gray-500">An entered email or phone replaces that reservation contact.</p>
                    </div>
                  </label>

                  {formData.guestAlertUseContactOverride && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label htmlFor="guestAlertEmailOverride" className="block text-xs font-medium text-gray-700">Replacement email</label>
                        <input
                          id="guestAlertEmailOverride"
                          name="guestAlertEmailOverride"
                          type="email"
                          value={formData.guestAlertEmailOverride}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        {errors.guestAlertEmailOverride && <p className="mt-1 text-xs text-red-600">{errors.guestAlertEmailOverride}</p>}
                      </div>
                      <div>
                        <label htmlFor="guestAlertPhoneOverride" className="block text-xs font-medium text-gray-700">Replacement phone</label>
                        <input
                          id="guestAlertPhoneOverride"
                          name="guestAlertPhoneOverride"
                          type="tel"
                          value={formData.guestAlertPhoneOverride}
                          onChange={handleInputChange}
                          placeholder="(843) 555-0123"
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        {errors.guestAlertPhoneOverride && <p className="mt-1 text-xs text-red-600">{errors.guestAlertPhoneOverride}</p>}
                      </div>
                    </div>
                  )}
                  {errors.guestAlertsEnabled && <p className="text-sm text-red-600">{errors.guestAlertsEnabled}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Work Order Custom Flags */}
        {canManageWorkOrderFlags() && workOrderFlagTemplates.length > 0 && (
          <div className="sm:col-span-6">
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <svg className="h-4 w-4 mr-1.5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Work Order Flags
            </label>
            <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              {workOrderFlagTemplates.map(template => {
                const isSelected = (formData.workOrderFlagTemplateIds || []).includes(template.id);
                const textColor = calculateTintedFlagTextColor(template.backgroundColor, isSelected ? 0.2 : 0.1);
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleWorkOrderFlagToggle(template.id)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : 'hover:opacity-80'
                    }`}
                    style={{
                      backgroundColor: `${template.backgroundColor}${isSelected ? '33' : '1A'}`,
                      color: textColor,
                      borderColor: `${template.backgroundColor}66`
                    }}
                  >
                    <FlagIcon iconName={template.iconName} className="h-4 w-4 mr-1" />
                    {template.flagValue}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Due Date */}
        <div className="sm:col-span-3">
          <label htmlFor="dueDate" className="flex items-center text-sm font-medium text-gray-700">
            <svg className="h-4 w-4 mr-1.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Due Date
          </label>
          <div className="mt-1">
            <input
              type="date"
              id="dueDate"
              name="dueDate"
              value={formatDateForInput(formData.dueDate)}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]} // Prevent past dates
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Set when this work order should be completed
            </p>
          </div>
        </div>

        {/* Legacy department markup retained hidden during the type migration. */}
        <div className="hidden" aria-hidden="true">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="h-4 w-4 mr-1.5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Assign to Department <span className="text-red-500 ml-1">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2 mt-1">
            Select a department to assign this work order to the appropriate roles
          </p>
          {errors.department && (
            <div className="mb-2 flex items-center">
              <svg className="h-5 w-5 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-600">{errors.department}</p>
            </div>
          )}
          <div className={`mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 ${errors.department ? 'border border-red-300 rounded-lg p-3' : ''}`}>
            <button
              type="button"
              onClick={() => handleDepartmentAssignment('property_management')}
              className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all duration-200 ${
                isDepartmentSelected('property_management')
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                  : errors.department
                  ? 'border-red-300 bg-white text-gray-700 hover:border-red-400 hover:bg-red-50'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md p-2 mr-3 ${
                    isDepartmentSelected('property_management') ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <svg className={`h-5 w-5 ${isDepartmentSelected('property_management') ? 'text-blue-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium">Property Management</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Property Manager &amp; Maintenance
                    </div>
                  </div>
                </div>
                {isDepartmentSelected('property_management') && (
                  <svg className="h-5 w-5 text-blue-500 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => handleDepartmentAssignment('housekeeping')}
              className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all duration-200 ${
                isDepartmentSelected('housekeeping')
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                  : errors.department
                  ? 'border-red-300 bg-white text-gray-700 hover:border-red-400 hover:bg-red-50'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-emerald-400 hover:bg-emerald-50/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-md p-2 mr-3 ${
                    isDepartmentSelected('housekeeping') ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <svg className={`h-5 w-5 ${isDepartmentSelected('housekeeping') ? 'text-emerald-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium">Housekeeping</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Housekeeper Manager
                    </div>
                  </div>
                </div>
                {isDepartmentSelected('housekeeping') && (
                  <svg className="h-5 w-5 text-emerald-500 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
          </div>
        </div>



        {/* Assignment Section - Only show for new work orders and specific roles */}
        {showAssignment && canViewAssignment() && (
          <>
            <div className="sm:col-span-6">
              <div className="border-t border-gray-200 pt-5">
                <h3 className="text-lg font-medium text-gray-900">Additional Assignment (Optional)</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Optionally assign this work order to a specific user or team. Housekeeping work orders can be assigned to housekeeping teams; other work orders can be assigned to property management teams. Property Care and Feedback are assigned automatically to the property manager.
                </p>
              </div>
            </div>

            <div className="sm:col-span-6">
              <div className="flex gap-6 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="work-order-assignment-type"
                    checked={formData.assignmentType !== 'team'}
                    onChange={() => handleAssignmentTypeChange('person')}
                    disabled={isPropertyManagerAssignedType(formData.workOrderType)}
                  />
                  Individual
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="work-order-assignment-type"
                    checked={formData.assignmentType === 'team'}
                    onChange={() => handleAssignmentTypeChange('team')}
                    disabled={isPropertyManagerAssignedType(formData.workOrderType)}
                  />
                  Team
                </label>
              </div>
            </div>

            {formData.assignmentType === 'team' ? (
              <div className="sm:col-span-3">
                <label htmlFor="assignedTeamId" className="block text-sm font-medium text-gray-700">
                  {formData.workOrderType === WorkOrderType.HOUSEKEEPING
                    ? 'Assign to Housekeeping Team'
                    : 'Assign to Property Management Team'}
                </label>
                <div className="mt-1">
                  <select
                    id="assignedTeamId"
                    name="assignedTeamId"
                    value={formData.assignedTeamId || ''}
                    onChange={handleTeamAssignmentChange}
                    disabled={isPropertyManagerAssignedType(formData.workOrderType)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">
                      {isPropertyManagerAssignedType(formData.workOrderType)
                        ? 'Automatically assigned to property manager'
                        : 'No team assigned'}
                    </option>
                    {(teamsData?.activeTeams || [])
                      .filter((team) => team.department)
                      .map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({getTeamDepartmentLabel(team.department)})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            ) : (
            <div className="sm:col-span-3">
              <label htmlFor="assignedToId" className="block text-sm font-medium text-gray-700">
                Assign to User (Direct Assignment)
                {selectedProperty?.area && (
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    (showing {selectedProperty.area.replace(/_/g, ' ').toLowerCase()} area staff only)
                  </span>
                )}
              </label>
              <div className="mt-1">
                <select
                  id="assignedToId"
                  name="assignedToId"
                  value={formData.assignedToId || ''}
                  onChange={handleAssignmentChange}
                  disabled={isPropertyManagerAssignedType(formData.workOrderType)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">
                    {isPropertyManagerAssignedType(formData.workOrderType)
                      ? 'Automatically assigned to property manager'
                      : 'No specific user assigned'}
                  </option>
                  {usersData?.users
                    ?.filter(user => {
                      // Must be active and assignable to work orders
                      if (!user.isActive || !canBeAssignedWorkOrders(user.role, user.areas)) {
                        return false;
                      }
                      
                      // Exclude HOUSEKEEPER role from direct assignment
                      if (user.role === UserRole.HOUSEKEEPER) {
                        return false;
                      }
                      
                      // If property has an area, filter by area
                      const propertyArea = selectedProperty?.area;
                      if (propertyArea && user.areas && Array.isArray(user.areas)) {
                        return user.areas.includes(propertyArea);
                      }
                      
                      // If property has no area or user has no areas, include them for backward compatibility
                      return true;
                    })
                    ?.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || `${user.firstName} ${user.lastName}`} ({UserRoleLabels[user.role] || user.role})
                        {selectedProperty?.area && user.areas && user.areas.includes(selectedProperty.area) && (
                          <span> • {selectedProperty.area.replace(/_/g, ' ')}</span>
                        )}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            )}

            {formData.assignedTeamId && (
              <div className="sm:col-span-6">
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-green-800">
                      <div>
                        <strong>Team Assignment:</strong> {(teamsData?.activeTeams || []).find((team) => String(team.id) === String(formData.assignedTeamId))?.name || 'Selected Team'}
                        <br />
                        <span className="text-green-600">Current team members will be notified and can pick up this work order.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.assignedToId && (
              <div className="sm:col-span-6">
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-green-800">
                      <div>
                        <strong>Direct Assignment:</strong> {usersData?.users?.find(u => u.id === formData.assignedToId)?.name || 'Selected User'}
                        <br />
                        <span className="text-green-600">This user is directly assigned and will receive notifications.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Photos Upload */}
        <div className="sm:col-span-6">
          <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
            <svg className="h-4 w-4 mr-1.5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Photos
          </label>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            {imagePreview ? (
              <div className="space-y-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mx-auto max-h-48 rounded-lg"
                />
                <p className="text-sm text-gray-500">
                  Uploading...
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="text-sm text-gray-600">
                  <span className="text-blue-600 hover:text-blue-500">
                    Click to upload
                  </span>{' '}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 10MB (up to 10 photos)
                </p>
              </div>
            )}
          </div>
          {isUploading && (
            <div className="mt-2">
              <div className="bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {photoUploads.length > 0 && (
            <div className="mt-3 space-y-2" aria-live="polite">
              {photoUploads.map((upload) => (
                <div key={upload.id} className="flex items-start justify-between gap-3 text-xs">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-700 truncate">{upload.name}</div>
                    <div className={upload.status === 'failed' ? 'text-red-600' : 'text-gray-500'}>
                      {upload.status === 'preparing' && 'Preparing photo from device…'}
                      {upload.status === 'uploading' && `Uploading… ${upload.progress}%`}
                      {upload.status === 'verifying' && 'Verifying uploaded photo…'}
                      {upload.status === 'retrying' && 'Connection interrupted—retrying once…'}
                      {upload.status === 'complete' && 'Upload verified'}
                      {upload.status === 'failed' && upload.error}
                    </div>
                  </div>
                  {upload.status === 'complete' && <span className="text-green-600 font-semibold">Ready</span>}
                </div>
              ))}
            </div>
          )}

          {isUploading && (
            <button
              type="button"
              onClick={() => uploadAbortControllerRef.current?.abort()}
              className="mt-3 text-xs font-medium text-red-600 hover:text-red-700"
            >
              Cancel photo upload
            </button>
          )}

          {photoUploadError && !isUploading && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Your work-order details are still here. Select the failed photo again when the connection is ready.
            </div>
          )}

          {/* Photo Gallery */}
          {formData.photos && formData.photos.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Photos</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {(formData.photos || []).map((photo, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={photo} 
                      alt={`Order photo ${index + 1}`}
                      className="h-24 w-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove photo"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Videos Upload */}
        <div className="sm:col-span-6">
          <VideoUploader
            videos={formData.videos || []}
            onVideosChange={(videos) => {
              setFormData(prev => ({
                ...prev,
                videos
              }));
            }}
            maxVideos={2}
            disabled={isLoading || isUploading}
          />
        </div>
      </div>

      {/* Hide buttons when requested */}
      {!hideButtons && (
        <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex justify-center items-center px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 shadow-sm transition-colors"
          >
            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </button>
          <button
            type="submit"
            id="work-order-submit-btn"
            disabled={isLoading || isUploading}
            className={`inline-flex justify-center items-center px-6 py-2.5 rounded-lg text-sm font-semibold text-white shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${
              isLoading || isUploading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg'
            }`}
          >
            {isLoading || isUploading ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                {isUploading ? 'Finishing Photo…' : 'Saving…'}
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={initialData ? 'M5 13l4 4L19 7' : 'M12 4v16m8-8H4'} />
                </svg>
                {initialData ? 'Update Work Order' : 'Create Work Order'}
              </>
            )}
          </button>
        </div>
      )}
    </form>
  );
});

export default WorkOrderForm;
